import { useState, useCallback, useEffect } from "react";

type AgentLogResponse = {
  paginationCursor?: string;
  content: string | null;
};

type LogState = {
  loading: boolean;
  loadingMore: boolean;
  error?: string;
  content: string;
  paginationCursor?: string;
  hasMoreData: boolean;
};

type Job = {
  id: string;
  basePath: string;
  state: string;
  hosted?: boolean;
  agentName?: string;
};

const parseLogError = (error: unknown): string => {
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return "Network error - please check your connection";
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "Failed to load agent logs";
};

const buildLogUrl = (basePath: string, cursor?: string): string => {
  const params = new URLSearchParams();
  if (cursor) {
    params.append("paginationCursor", cursor);
  }
  return `${basePath}/agent_log?${params.toString()}`;
};

const handleApiError = async (response: Response): Promise<never> => {
  let errorData: { error?: string };
  try {
    errorData = await response.json();
  } catch {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const errorMessage = errorData.error || response.statusText;

  switch (response.status) {
    case 422:
      throw new Error(errorMessage);
    case 503:
      throw new Error(`Service temporarily unavailable: ${errorMessage}`);
    case 404:
      throw new Error("Job not found or logs not available");
    case 403:
      throw new Error("Access denied to agent logs");
    default:
      throw new Error(`Request failed: ${errorMessage}`);
  }
};

export function useAgentLogs(job: Job) {
  const [state, setState] = useState<LogState>({
    loading: true,
    loadingMore: false,
    content: "",
    hasMoreData: true,
  });

  const fetchLogs = useCallback(
    async (cursor?: string) => {
      const isInitialLoad = !cursor;
      setState((prev) => ({
        ...prev,
        loading: isInitialLoad,
        loadingMore: !isInitialLoad,
        error: undefined,
      }));

      try {
        const response = await fetch(buildLogUrl(job.basePath, cursor));

        if (!response.ok) {
          await handleApiError(response);
        }

        const data: AgentLogResponse = await response.json();

        setState((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false,
          content:
            cursor && data.content ? prev.content + data.content : data.content || prev.content,
          paginationCursor: data.paginationCursor,
          hasMoreData: Boolean(data.paginationCursor),
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          loadingMore: false,
          error: parseLogError(error),
        }));
      }
    },
    [job.basePath],
  );

  const loadMore = useCallback(() => {
    if (state.paginationCursor && !state.loadingMore && !state.loading) {
      fetchLogs(state.paginationCursor);
    }
  }, [fetchLogs, state.paginationCursor, state.loadingMore, state.loading]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return {
    ...state,
    loadMore,
  };
}
